import React from 'react';
import { observer } from 'mobx-react';
import { ReactComponent as LocationIcon } from 'Assets/svg/location.svg';
import { ReactComponent as OrganizationIcon } from 'Assets/svg/organization.svg';
import { ReactComponent as EmptyCompanyLogo } from 'Assets/svg/empty-company-logo.svg';
import advSearchState from 'Stores/advSearch';
import { Utils } from 'Utils/utils';
import * as CONSTANTS from 'Utils/constants';
import { LazyImage } from '../lazyImage';
import { CompanyName } from '../CompanyList/companyName';
import { CompanyIndustry } from '../CompanyList/companyIndustry';
import { CompanyLocation } from '../CompanyList/companyLocation';
import { SocialLinks } from '../SocialLinks';
import { CompanyRevenue } from '../CompanyList/companyRevenue';
import { CompanyEmployeeCount } from '../CompanyList/companyEmployeeCount';
import { CompanyWebsite } from '../CompanyList/companyWebsite';
import Dropdown from '../dropdown';
import { PhoneItem } from './ContactPhone';

const CompanyCard = observer(props => {
  const { companyCardData, showCompanyCardLoading } = props;
  const {
    name,
    logo,
    industry,
    revenue,
    employeeCount,
    website,
    city,
    state,
    country,
    primaryDomain,
  } = companyCardData;

  const CompanyCardInfoDataLoadingPlaceHolder = () => {
    return (
      <div className="contact-card company-card loading-placeholder">
        <span className="sharp-point" />
        <div className="contact-card-details">
          <div className="basic-info-wrapper">
            <div className="company-logo-wrapper linear-background" />
            <div className="company-name-location-wrap">
              <CompanyName parentClass="linear-background" />
              <div className="company-phone-location">
                <CompanyLocation city="loading" clsName="linear-background" />
              </div>
            </div>
          </div>
          <div className="additional-info-wrapper">
            <CompanyIndustry industry="loading" clsName="linear-background" />
            <CompanyRevenue revenue="loading" clsName="linear-background" />
            <CompanyEmployeeCount
              employeeCount="loading"
              clsName="linear-background"
            />
            <CompanyWebsite
              primaryDomain="loading"
              clsName="linear-background"
            />
          </div>
        </div>
      </div>
    );
  };

  const CompanyCardInfoData = () => {
    return (
      <div className="contact-card company-card">
        <span className="sharp-point" />
        <div className="contact-card-details">
          <div className="basic-info-wrapper">
            <LazyImage
              alt={name}
              src={logo}
              clsName="logo"
              threshold={200}
              parentClass="company-logo-wrapper"
            />
            <div className="company-name-location-wrap">
              <CompanyName name={name} />
              <div className="company-phone-location">
                <CompanyLocation city={city} state={state} country={country} />
              </div>
            </div>
          </div>
          <div className="additional-info-wrapper">
            <CompanyIndustry industry={industry} />
            <CompanyRevenue revenue={revenue} />
            <CompanyEmployeeCount employeeCount={employeeCount} />
            <CompanyWebsite primaryDomain={primaryDomain} />
          </div>
          <SocialLinks {...companyCardData} />
        </div>
      </div>
    );
  };

  return showCompanyCardLoading ? (
    <CompanyCardInfoDataLoadingPlaceHolder />
  ) : (
    <CompanyCardInfoData />
  );
});

const ContactCompany = observer(props => {
  const {
    cname,
    companyCountry,
    companyState,
    companyCity,
    companyId,
    companyLogo,
    showLogo = false,
    companyPhone,
  } = props;

  let companyStr = '';
  if (companyCity) companyStr += companyCity;
  if (companyState)
    if (companyStr !== '') companyStr += `, ${companyState}`;
    else companyStr += companyState;
  if (companyCountry)
    if (companyStr !== '') companyStr += `, ${companyCountry}`;
    else companyStr += companyCountry;

  const [showCompanyCardLoading, setShowCompanyCardLoading] = React.useState(
    false,
  );
  const [companyCardData, setCompanyCardData] = React.useState({});
  const onMouserEnterCompanyCard = (e, comId = null) => {
    window.timeoutVar = setTimeout(() => {
      setShowCompanyCardLoading(true);
      const companyData = advSearchState.getCompanyCardData(comId);

      Utils.mixpanelTrack(
        CONSTANTS.MX_AS_EVENTS.LB_RESULT_HOVER_COMPANY_NAME,
        {},
      );
      if (companyData !== null) {
        setCompanyCardData(companyData);
        setShowCompanyCardLoading(false);
      } else {
        const companyInfoCallback = cData => {
          if (cData !== null) {
            setCompanyCardData(cData);
            setShowCompanyCardLoading(false);
            advSearchState.setCompanyCardData(comId, cData);
          }
        };
        advSearchState.getCompanyInfoById(comId, companyInfoCallback);
      }
    }, 500);
  };

  const onMouserLeaveCompanyCard = (e, comId) => {
    if (window.timeoutVar !== undefined) clearTimeout(window.timeoutVar);
    setCompanyCardData({});
    setShowCompanyCardLoading(false);
  };

  return cname ? (
    <div className="company-name">
      {showLogo &&
        (companyLogo ? (
          <div className="logo-section">
            <div className="logo-wrapper">
              <img src={companyLogo} alt={`${cname} logo`} />
            </div>
          </div>
        ) : (
          <div className="logo-wrapper">
            <EmptyCompanyLogo className="empty-company-logo" />
          </div>
        ))}
      <div className="text-section">
        {companyId ? (
          <div
            className={`company-name-container${
              companyStr || companyPhone ? ' has-other-data' : ''
            }`}
            onMouseEnter={e => onMouserEnterCompanyCard(e, companyId)}
            onMouseLeave={e => onMouserLeaveCompanyCard(e, companyId)}
          >
            <Dropdown
              toggleElement={<div className="name">{cname}</div>}
              dropdownHeight={250}
              showOnHover
            >
              {(Object.keys(companyCardData).length > 0 ||
                showCompanyCardLoading) && (
                <CompanyCard
                  companyCardData={companyCardData}
                  showCompanyCardLoading={showCompanyCardLoading}
                />
              )}
            </Dropdown>
          </div>
        ) : (
          <div className="name">{cname}</div>
        )}
        {companyStr && (
          <div className="company-location">
            <LocationIcon />
            <div
              className="company-address-info show-elipsis"
              title={companyStr}
            >
              {companyStr}
            </div>
          </div>
        )}
        {companyPhone && (
          <div className="contact-phone">
            <PhoneItem
              type="company"
              phoneNumber={companyPhone}
              pageName="advsearch"
              Icon={OrganizationIcon}
            />
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className="company-name">Unavailable</div>
  );
});

export { ContactCompany };
export default ContactCompany;
